import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  Plus, Search, Mail, Trash2, Power, 
   RotateCcw, AlertTriangle, Edit2
} from 'lucide-react';

import { usePermission } from '../../hooks/usePermission';

const CustomersPage = () => {
  const queryClient = useQueryClient();
  const { can } = usePermission(); 

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [viewTrash, setViewTrash] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '', email: '', password: '', 
    country: '', city: '', mobile_number: '', company_name: '',
    assign_package_id: '', payment_method: 'cash', coupon_code: ''
  });

  // Queries
  const { data } = useQuery({ 
    queryKey: ['customers', search, page, viewTrash], 
    queryFn: () => axiosClient.get('/admin/customers', { params: { search, page, limit: 10, trash: viewTrash } }),
    keepPreviousData: true
  });
  const { customers = [], total = 0 } = data || {};
  
  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/customers/create', data),
    onSuccess: () => { queryClient.invalidateQueries(['customers']); setShowForm(false); resetForm(); }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => axiosClient.put(`/admin/customers/${id}`, data),
    onSuccess: () => { queryClient.invalidateQueries(['customers']); setShowForm(false); resetForm(); }
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, permanent }) => axiosClient.delete(`/admin/customers/${id}`, { params: { permanent } }),
    onSuccess: () => queryClient.invalidateQueries(['customers'])
  });

  const restoreMutation = useMutation({
    mutationFn: (id) => axiosClient.post(`/admin/customers/${id}/restore`),
    onSuccess: () => queryClient.invalidateQueries(['customers'])
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }) => axiosClient.put(`/admin/customers/${id}/status`, { is_active: status }),
    onSuccess: () => {
        // Force Refetch of the list immediately
        queryClient.invalidateQueries({ queryKey: ['customers'] });
        // Optional: Alert removed for smoother experience, status badge will update automatically
    },
    onError: (err) => alert("Failed to change status: " + err.message)
  });

  const resetForm = () => { setShowForm(false); setEditingId(null); setFormData({ full_name: '', email: '', password: '', country: '', city: '', mobile_number: '', company_name: '', assign_package_id: '', payment_method: 'cash', coupon_code: '' }); };

  const handleEdit = (customer) => {
      setFormData({
          full_name: customer.full_name, email: customer.email, mobile_number: customer.mobile_number||'', company_name: customer.company_name||'', city: customer.city||'', country: customer.country||'', password: '', assign_package_id: '', payment_method: 'cash', coupon_code: ''
      });
      setEditingId(customer.id);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (id) => {
    if (viewTrash) {
      if (window.confirm("WARNING: PERMANENTLY DELETE?")) deleteMutation.mutate({ id, permanent: true });
    } else {
      if (window.confirm("Move to Trash?")) deleteMutation.mutate({ id, permanent: false });
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Customer Directory</h1>
          <p className="text-sm text-slate-500">{viewTrash ? 'Trash Bin' : 'Active Customers'} • Total {total}</p>
        </div>
        <div className="flex space-x-2 w-full md:w-auto">
          {/* ✅ Check Permission for Deleting/Viewing Trash */}
          {can('customers:delete') && (
            <div className="bg-slate-100 p-1 rounded-lg flex text-xs font-bold">
                <button onClick={() => { setViewTrash(false); setPage(1); }} className={`px-3 py-1.5 rounded-md transition-all ${!viewTrash ? 'bg-white shadow text-indigo-600' : 'text-slate-500'}`}>ACTIVE</button>
                <button onClick={() => { setViewTrash(true); setPage(1); }} className={`px-3 py-1.5 rounded-md transition-all flex items-center ${viewTrash ? 'bg-white shadow text-red-600' : 'text-slate-500'}`}><Trash2 className="w-3 h-3 mr-1"/> TRASH</button>
            </div>
          )}

          <div className="relative flex-1">
            <input className="pl-9 pr-4 py-2 border rounded-lg text-sm w-full md:w-64" placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          </div>
          
          {/* ✅ Check Permission for Create */}
          {!viewTrash && can('customers:create') && (
            <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-indigo-700 shadow-lg text-sm font-bold">
              <Plus className="w-4 h-4 mr-2" /> {showForm ? 'Close' : 'Add'}
            </button>
          )}
        </div>
      </div>

      {/* Form (Only show if permitted) */}
      {!viewTrash && showForm && (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-indigo-100 mb-6">
          <form onSubmit={(e) => { e.preventDefault(); editingId ? updateMutation.mutate({id: editingId, data: formData}) : createMutation.mutate({...formData, assign_package_id: parseInt(formData.assign_package_id)||null}); }}>
             {/* ... (Fields remain same as before, simplified for brevity here) ... */}
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <input placeholder="Full Name *" className="p-2 border rounded" required value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} />
                 <input placeholder="Email *" className="p-2 border rounded" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                 {/* ... other inputs ... */}
             </div>
             <button className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded shadow-lg">{editingId ? 'Update' : 'Create'}</button>
          </form>
        </div>
      )}

      {/* List */}
      <div className="grid grid-cols-1 gap-4">
        {customers.map((c) => (
          <div key={c.id} className={`p-4 rounded-xl shadow-sm border flex flex-col md:flex-row items-center justify-between hover:shadow-md transition-all duration-200 ${viewTrash ? 'bg-red-50 border-red-100 opacity-80' : 'bg-white border-slate-100'}`}>
             
             {/* ... User Info Section ... */}
             <div className="flex items-center space-x-4 w-full md:w-auto mb-4 md:mb-0">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg ${viewTrash ? 'bg-slate-400' : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-indigo-500/20'}`}>
                   {c.initials}
                </div>
                <div>
                   <h3 className="font-bold text-slate-800">{c.full_name}</h3>
                   <div className="flex items-center text-sm text-slate-500 space-x-3 flex-wrap">
                      <span className="flex items-center"><Mail className="w-3 h-3 mr-1"/> {c.email}</span>
                      {c.mobile_number && <span className="flex items-center text-slate-400">| {c.mobile_number}</span>}
                   </div>
                   {viewTrash && <p className="text-xs text-red-500 mt-1 flex items-center"><Trash2 className="w-3 h-3 mr-1"/> Deleted on: {new Date(c.deleted_at).toLocaleDateString()}</p>}
                </div>
             </div>

             {/* ... Actions Section ... */}
             <div className="flex items-center space-x-4 w-full md:w-auto justify-between md:justify-end">
                {!viewTrash && (
                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {c.is_active ? 'Active' : 'Suspended'}
                    </span>
                )}
                
                <div className="flex space-x-2">
                   {viewTrash ? (
                      <>
                        <button onClick={() => restoreMutation.mutate(c.id)} className="p-2 bg-white border border-green-200 text-green-600 rounded-lg hover:bg-green-50 flex items-center text-xs font-bold px-3">
                           <RotateCcw className="w-3 h-3 mr-2"/> Restore
                        </button>
                        <button onClick={() => handleDelete(c.id)} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center text-xs font-bold px-3 shadow-lg shadow-red-500/30">
                           <AlertTriangle className="w-3 h-3 mr-2"/> DESTROY
                        </button>
                      </>
                   ) : (
                      <>
                        {/* Add Visual Toggle Button */}
                        {can('customers:edit') && (
                            <>
                                <button 
                                    onClick={() => toggleStatusMutation.mutate({ id: c.id, status: !c.is_active })} 
                                    disabled={toggleStatusMutation.isLoading}
                                    title={c.is_active ? "Suspend User" : "Activate User"}
                                    className={`p-2 rounded-lg transition-colors ${c.is_active ? 'bg-green-50 text-green-600 hover:bg-red-50 hover:text-red-600' : 'bg-red-50 text-red-600 hover:bg-green-50 hover:text-green-600'}`}
                                >
                                    <Power className="w-4 h-4" />
                                </button>
                                
                                <button onClick={() => handleEdit(c)} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors border border-blue-100">
                                    <Edit2 className="w-4 h-4" />
                                </button>
                            </>
                        )}
                        
                        {can('customers:delete') && (
                            <button onClick={() => handleDelete(c.id)} className="p-2 bg-red-50 text-red-600 rounded-lg border border-red-100 transition-colors hover:bg-red-100">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        )}
                      </>
                   )}
                </div>
             </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomersPage;